import { UserManager, type SigninRedirectArgs } from 'oidc-client-ts';
import { clearAllPaginationStates } from 'app/utils/helpers/pagination-storage';
import { clearAllPartiesPaginationStates } from 'app/utils/helpers/parties-pagination-storage';
import { WALLET_TOKEN_STORE_KEY, SCAN_PERSIST_KEY, VALIDATOR_PERSIST_KEY } from '../../constants';
import { authConfig } from './config';

/** Session key: path to restore after OIDC redirect login (`redirect_uri` is fixed to `origin/`). */
export const POST_LOGIN_RETURN_PATH_KEY = 'canton_console_ui_post_login_return';

const isSafeInternalReturnPath = (path: string): boolean => {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  try {
    const resolved = new URL(path, window.location.origin);
    return resolved.origin === window.location.origin;
  } catch {
    return false;
  }
};

export const consumePostLoginReturnPath = (): string | null => {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_RETURN_PATH_KEY);
    sessionStorage.removeItem(POST_LOGIN_RETURN_PATH_KEY);
    if (!raw || !isSafeInternalReturnPath(raw)) return null;
    return raw;
  } catch {
    return null;
  }
};

// TODO: for testing auth providers
export const userManager = new UserManager(authConfig);

const originalSigninRedirect = userManager.signinRedirect.bind(userManager);
userManager.signinRedirect = async function signinRedirectWithReturnCapture(args?: SigninRedirectArgs) {
  try {
    const path = `${window.location.pathname}${window.location.search}`;
    if (path !== '/' || window.location.search) {
      sessionStorage.setItem(POST_LOGIN_RETURN_PATH_KEY, path);
    } else {
      sessionStorage.removeItem(POST_LOGIN_RETURN_PATH_KEY);
    }
  } catch {
    // ignore storage failures (private mode, quota)
  }
  return originalSigninRedirect(args);
};

export async function getUser() {
  const user = await userManager.getUser();
  return user;
}

export async function getAccessToken() {
  const user = await getUser();
  return user?.access_token;
}

/** Clears app-owned storage before OIDC `signoutRedirect` / `removeUser`. Safe to call from UI before `useAuth().signoutRedirect()`. */
export function clearClientSessionBeforeSignOut(): void {
  clearAllPaginationStates();
  clearAllPartiesPaginationStates();
  localStorage.removeItem(SCAN_PERSIST_KEY);
  localStorage.removeItem(VALIDATOR_PERSIST_KEY);

  try {
    localStorage.removeItem(WALLET_TOKEN_STORE_KEY);
  } catch {
    // ignore
  }

  try {
    sessionStorage.removeItem(POST_LOGIN_RETURN_PATH_KEY);
  } catch {
    // ignore
  }
}

/**
 * Ends the Keycloak/browser session and clears local OIDC storage.
 * Prefer calling `clearClientSessionBeforeSignOut()` then `useAuth().signoutRedirect()` in React so auth context stays in sync.
 */
export async function logout() {
  clearClientSessionBeforeSignOut();
  await userManager.clearStaleState();
  await userManager.signoutRedirect();
}

export async function isAuthenticated() {
  const token = await getAccessToken();

  return !!token;
}

export async function handleOAuthCallback(callbackUrl) {
  try {
    const user = await userManager.signinRedirectCallback(callbackUrl);
    return user;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(`error while handling oauth callback: ${e}`);
    return null;
  }
}

export async function sendOAuthRequest() {
  return userManager.signinRedirect();
}

// renews token using refresh token
export async function renewToken() {
  const user = await userManager.signinSilent();

  return user;
}

// userManager.events.addUserLoaded((user) => {
//   // Custom logic after successful login
//   console.log(user);
// });

// This function is used to access token claims
// `.profile` is available in Open Id Connect implementations
// in simple OAuth2 it is empty, because UserInfo endpoint does not exist
// export async function getRole() {
//     const user = await getUser();
//     return user?.profile?.role;
// }

// This function is used to change account similar way it is done in Google
// export async function selectOrganization() {
//     const args = {
//         prompt: "select_account"
//     }
//     await userManager.signinRedirect(args);
// }

// useEffect(() => {
//   // the `return` is important - addAccessTokenExpiring() returns a cleanup function
//   return events.addAccessTokenExpiring(() => {
//     // You're about to be signed out due to inactivity. Press continue to stay signed in.
//     signinSilent();
//   })
// }, [events, signinSilent]);
